import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  useTheme,
  Alert
} from '@mui/material';
import {
  TrendingUp,
  Help,
  Assignment,
  Info,
  Warning,
  Check,
  TrendingDown,
  ArrowCircleDown,
  StarRate,
  Lightbulb as RecommendationsIcon
} from '@mui/icons-material';
import { getSignalColor } from '../../theme/themesV3';

interface MarketRecommendationsProps {
  reportData: any;
  onTickerSelect?: (ticker: string) => void;
  initialTab: string;
  renderMarketRating: (rating: string) => React.ReactElement;
  renderMarketTrend: (trend: string) => React.ReactElement;
}

const MarketRecommendations: React.FC<MarketRecommendationsProps> = ({
  reportData,
  onTickerSelect,
  initialTab,
  renderMarketRating,
  renderMarketTrend
}) => {
  const theme = useTheme();

  if (!reportData?.market_report) {
    return (
      <Box sx={{ textAlign: 'center', p: 3 }}>
        <Typography variant="body1">No market report data available</Typography>
      </Box>
    );
  }

  const marketData = reportData.market_report;
  const recommendations = marketData.InvestmentRecommendations || [];

  const investRecommendations = recommendations.filter(
    (rec: any) => rec.RecommendationType === 'invest'
  );

  const considerRecommendations = recommendations.filter(
    (rec: any) => rec.RecommendationType === 'consider'
  );

  const reduceRecommendations = recommendations.filter(
    (rec: any) => rec.RecommendationType === 'reduce'
  );

  const renderRecommendationCard = (rec: any, index: number) => {
    const getRecommendationColor = () => {
      switch (rec.RecommendationType) {
        case 'invest': return getSignalColor("Moderately Bullish");
        case 'reduce': return getSignalColor("Moderately Bearish");
        default: return getSignalColor("Neutral");
      }
    };

    const getRecommendationIcon = () => {
      switch (rec.RecommendationType) {
        case 'invest': return <TrendingUp />;
        case 'reduce': return <ArrowCircleDown />;
        default: return <Help />;
      }
    };

    // const getRecommendationChipColor = () => {
    //   switch (rec.RecommendationType) {
    //     case 'invest': return 'success';
    //     case 'reduce': return 'error';
    //     default: return 'warning';
    //   }
    // };
    
    return (
      <Grid item xs={12} md={6} key={index}>
        <Card elevation={3} sx={{ 
          height: '100%', 
          display: 'flex',
          flexDirection: 'column',
          borderLeft: `5px solid ${getSignalColor(rec.RecommendationType)}`
        }}>
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="h6" fontWeight="bold" color={theme.palette.secondary.main} gutterBottom>
                {rec.Details.Title}
              </Typography>
              <Chip 
                icon={getRecommendationIcon()}
                label={rec.RecommendationType.toUpperCase()}
                size="small"
                //color={theme.palette.secondary.main}
                sx={{ 
                  fontWeight: 'bold',
                  bgcolor: getSignalColor(rec.RecommendationType)
                }}
              />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1" paragraph>
              {rec.Details.Reason || 'No detailed reasoning provided.'}
            </Typography>
            {rec.Details.Risks && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" color={getSignalColor("Bearish")} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Warning fontSize="small" sx={{ mr: 0.5 }} />
                  Risks:
                </Typography>
                <Typography variant="body2">
                  {rec.Details.Risks}
                </Typography>
              </Box>
            )}
            {rec.Details.ComplementaryInvestment && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" color={theme.palette.primary.main} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Info fontSize="small" sx={{ mr: 0.5 }} />
                  Additional Guidance:
                </Typography>
                <Typography variant="body2">
                  {rec.Details.ComplementaryInvestment}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box>
      {/* Main header */}
      <Paper sx={{ p: 3, mb: 4, bgcolor: theme.palette.primary.main, color: 'white' }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom
        sx={{
          display: 'flex', 
          alignItems: 'center',
        }}>
          <RecommendationsIcon sx={{ mr: 1, color: theme.palette.custom.iconSecondary }}/>
          Investment Recommendations
        </Typography>
        <Typography variant="body1">
          Based on current market analysis, we've identified the following investment opportunities and considerations.
        </Typography>
      </Paper>

      {/* Invest Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ 
          display: 'flex', 
          alignItems: 'center',
          color: getSignalColor('invest'),
          mb: 2,
          pb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <TrendingUp sx={{ mr: 1 }} />
          INVEST: Recommended Investment Opportunities
        </Typography>
        
        <Grid container spacing={3}>
          {investRecommendations.length > 0 ? (
            investRecommendations.map((rec: any, index: number) => renderRecommendationCard(rec, index))
          ) : (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ p: 3 }}>
                No investment recommendations available at this time.
              </Alert>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Consider Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ 
          display: 'flex', 
          alignItems: 'center',
          color: getSignalColor('consider'),
          mb: 2,
          pb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <Help sx={{ mr: 1 }} />
          CONSIDER: Potential Opportunities to Evaluate
        </Typography>
        
        <Grid container spacing={3}>
          {considerRecommendations.length > 0 ? (
            considerRecommendations.map((rec: any, index: number) => renderRecommendationCard(rec, index))
          ) : (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ p: 3 }}>
                No considerations available at this time.
              </Alert>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Reduce Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ 
          display: 'flex', 
          alignItems: 'center',
          color: getSignalColor('reduce'),
          mb: 2,
          pb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <ArrowCircleDown sx={{ mr: 1 }} />
          REDUCE: Areas to Consider Reducing Exposure
        </Typography>
        
        <Grid container spacing={3}>
          {reduceRecommendations.length > 0 ? (
            reduceRecommendations.map((rec: any, index: number) => renderRecommendationCard(rec, index))
          ) : (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ p: 3 }}>
                No recommendations to reduce exposure at this time.
              </Alert>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Display recommended tickers if available */}
      {marketData.tickers_meeting_investment_profile && 
       marketData.tickers_meeting_investment_profile.length > 0 ? (
        <Paper elevation={3} sx={{ p: 3, mt: 4, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <StarRate sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
            Recommended Tickers
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {marketData.tickers_meeting_investment_profile.map((ticker: string) => (
              <Chip
                key={ticker}
                label={ticker}
                onClick={() => onTickerSelect && onTickerSelect(ticker)}
                clickable
                sx={{ 
                  fontWeight: 'bold',
                  //backgroundColor: theme.palette.primary.light,
                  backgroundColor: theme.palette.custom.bannerBg,
                  color: theme.palette.primary.contrastText
                }}
              />
            ))}
          </Box>
        </Paper>
      ) : (
        <Paper elevation={3} sx={{ p: 3, mt: 4, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <StarRate sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
            Recommended Tickers
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Alert severity="info">
            No recommended tickers available for this investment profile.
          </Alert>
        </Paper>
      )}
    </Box>
  );
};

export default MarketRecommendations;